import { isPNDataType } from '@pn/core/domain/data';
import {
  buildQuery,
  getExportableQuery,
  type Query,
} from '@pn/core/domain/query';
import {
  createInitInternalState,
  getLayerId,
  ItemsFolder,
  type IWorkspaceItemMapper,
  type WorkspaceItem,
} from '@pn/core/domain/workspace';
import { getCurrentUserId } from '@pn/core/storage/user/currentUserAccessors';
import { getMapping } from '@pn/core/storage/workspace/mappings';
import { pnWorkspaceItems } from '@pn/core/storage/workspace/pnWorkspaceItems';
import { nullToUndefined, undefinedToNull } from '@pn/core/utils/logic';
import { apiLayerItemMapper } from '@pn/services/api/layer/apiLayerMapper';
import {
  apiQueryMapper,
  injectWhereAdditions,
} from '@pn/services/api/query/apiQueryMapper';
import {
  getReferencedListId,
  toListReferencePredicate,
  toListReferenceSql,
} from '@pn/services/api/query/listReferenceSql';
import { apiProjectUserMapper } from '@pn/services/api/user/apiUserMapper';
import { appSignalErrorLogger } from '@pn/services/loggers/appSignalErrorLogger';
import { mapboxLayerMapper } from '@pn/services/map/mapbox/mappers/mapboxLayerMapper';
import { generateEmptyGeoJsonFeatureCollection } from '@pn/services/utils/geojson';
import { isNil } from 'lodash-es';
import type { ExtendedMapboxLayer } from '../layer/types';
import type { ApiList, ApiListPayload } from './types';

export const apiListMapper: IWorkspaceItemMapper<ApiList, ApiListPayload> = {
  toWorkspaceItem: (apiList) => {
    if (isNil(apiList.source)) {
      throw new Error(`List source is missing: ${apiList.id}`); // this should never happen but unfortunately it does
    }

    const userId = getCurrentUserId();

    const sourceItem = isPNDataType(apiList.source.id)
      ? pnWorkspaceItems[apiList.source.id]
      : apiLayerItemMapper.toWorkspaceItem(apiList.source);

    const sourceListId = getReferencedListId(apiList.query.sql);

    return {
      folder: getFolder(apiList),
      isTemporary: apiList.origin === 'stackdx', // hide Stack source lists from the Library
      id: apiList.id,
      dataType: apiList.source.id,
      name: apiList.name,
      numberOfElements: nullToUndefined(apiList.count),
      sourceItem,
      itemType: 'list',
      origin: apiList.origin,
      createdAt: apiList.created_at,
      updatedAt: apiList.updated_at,
      createdBy: !isNil(apiList.user)
        ? apiProjectUserMapper.toDomainProjectUser(apiList.user)
        : undefined,
      isShared: apiList.shared,
      isGlobal: apiList.global,
      map: {
        layers: (
          apiList.layers_definition_override ?? apiList.layers_definition
        ).map((mapboxLayer, index) =>
          mapMapboxLayerToDomainLayer(sourceItem, apiList, mapboxLayer, index)
        ),
        originalLayers:
          apiList.user?.id === userId
            ? undefined
            : apiList.layers_definition.map((mapboxLayer, index) =>
                mapMapboxLayerToDomainLayer(
                  sourceItem,
                  apiList,
                  mapboxLayer,
                  index
                )
              ),
      },
      dataSource: sourceItem.dataSource,
      detailsSource: sourceItem.detailsSource,
      query: {
        ...buildQuery({
          id: apiList.id,
          dataType: apiList.source.id,
        }),
        sql: isNil(sourceListId)
          ? nullToUndefined(apiList.query.sql)
          : undefined,
        sourceListId,
        filters: apiList.query.filters,
        filtersLinkOperator: apiList.query.filters_link_operator,
        scopeIds: apiList.query.requested_ids,
        geometry: nullToUndefined(apiList.query.geometry),
      },
      ...createInitInternalState({
        isVisualized: false,
        /* List references always need the list's current ids loaded. */
        isLoaded: apiList.query.is_loaded && isNil(sourceListId),
      }),
    };
  },
  toOriginalItem: (item) => {
    const query = toPersistedQuery(item.query);

    return {
      name: item.name,
      data_type: item.dataType,
      query: {
        sql: undefinedToNull(toStoredSql(query)),
        filters: query.filters,
        filters_link_operator: query.filtersLinkOperator,
        requested_ids: query.scopeIds,
        geometry: undefinedToNull(query.geometry),
      },
      resolved_sql: toResolvedSql(item.dataType, query),
      layers_definition: toApiListLayersDefinition(item),
    };
  },
};

export function toApiListLayersDefinition(
  item: WorkspaceItem
): ExtendedMapboxLayer[] {
  return item.map.layers.map((layer) => ({
    ...mapboxLayerMapper.toTargetLayer(layer),
    id: '', // not used
    name: layer.name,
    render_as_points: layer.renderAsPoints,
  }));
}

/**
 * With a `sourceListId`, `scopeIds` are only the list's members at load time.
 * Storing them would freeze those members.
 */
function toPersistedQuery(query: Query): Query {
  if (isNil(query.sourceListId)) return query;

  return { ...query, scopeIds: [] };
}

/**
 * The API has no field for `sourceListId`, so it's stored as a `{{list:<id>}}`
 * placeholder in `sql` and read back by `getReferencedListId`. Combined with
 * custom SQL, it's ANDed into it and stays part of that SQL once loaded again.
 */
function toStoredSql(query: Query): string | undefined {
  if (isNil(query.sourceListId)) return query.sql;
  if (isNil(query.sql)) return toListReferenceSql(query.sourceListId);

  return injectWhereAdditions(
    query.sql,
    toListReferencePredicate(query.sourceListId)
  );
}

/**
 * Flattens the query into standalone SQL for the API to store alongside the
 * structured query. `{{list:<id>}}` placeholders are left in for the API to
 * expand whenever the stored SQL is run.
 */
function toResolvedSql(dataType: string, query: Query): string | null {
  const mapping = getMapping(dataType);
  if (isNil(mapping)) return null;

  try {
    return apiQueryMapper(mapping).toTargetQuery(
      getExportableQuery({ ...query, fields: ['internal_id'] })
    ).sql;
  } catch (error) {
    appSignalErrorLogger.logGenericError(
      error instanceof Error ? error : new Error(String(error)),
      getCurrentUserId(),
      'toResolvedSql'
    );
    return null;
  }
}

function getFolder(apiList: ApiList): WorkspaceItem['folder'] {
  const userId = getCurrentUserId();

  if (apiList.global) {
    return undefined; // do not show in the Library outside of projects
  } else if (apiList.origin === 'stackdx') {
    return ItemsFolder.Stack;
  } else if (apiList.origin === 'boe_intel') {
    return ItemsFolder.Intel;
  } else if (apiList.origin === 'arcgis') {
    return ItemsFolder.ArcGIS;
  } else if (!isNil(userId) && apiList.user?.id === userId) {
    return ItemsFolder.Personal;
  } else {
    return apiList.shared ? ItemsFolder.Shared : undefined;
  }
}

function mapMapboxLayerToDomainLayer(
  sourceItem: WorkspaceItem,
  apiList: ApiList,
  mapboxLayer: ExtendedMapboxLayer,
  index: number
) {
  return {
    name: mapboxLayer.name,
    ...sourceItem.map.layers[index],
    ...mapboxLayerMapper.toDomainLayer(
      {
        source: {
          type: 'geojson',
          data: generateEmptyGeoJsonFeatureCollection(),
        },
        'source-layer': undefined,
        ...mapboxLayer, // will override the source if one is present
        id: getLayerId(sourceItem.id, apiList.id, index),
      },
      mapboxLayer.render_as_points
    ),
  };
}
